import { Platform } from 'react-native';

import type { AssetsUploadAssetBody } from '@/api/WhisApi/generated/model';
import type { UploadedDocument } from '@/types/formField.types';

/**
 * Builds the value appended to FormData for an asset upload.
 *
 * The implementation is platform-dependent on purpose:
 * - Web (react-native-web): the browser's native FormData requires a real
 *   Blob/File. We fetch the document to get its binary content and wrap it in
 *   a File. Expo's FormData patch is NOT installed on web, so a File is safe.
 * - Native (iOS/Android): we must NOT build a React Native `File`, because
 *   Expo's `FormData.append` patch tries to assign `file.name`, which throws
 *   "Cannot assign to property 'name' which has only a getter" (RN `File.name`
 *   is a getter-only accessor). Instead we pass React Native's proprietary
 *   `{ uri, name, type }` descriptor, which native networking handles directly.
 */
export const buildUploadFile = async (
  document: UploadedDocument,
): Promise<AssetsUploadAssetBody['file']> => {
  if (Platform.OS === 'web') {
    const response = await fetch(document.uri);

    if (!response.ok) {
      throw new Error(`Failed to read document ${document.name}`);
    }

    const blob = await response.blob();
    const mimeType = document.mimeType ?? blob.type;

    return new File([blob], document.name, {
      type: mimeType || 'application/octet-stream',
    });
  }

  return {
    uri: document.uri,
    name: document.name,
    type: document.mimeType ?? 'application/octet-stream',
  } as unknown as Blob;
};
